export type NormalizedRole = "admin" | "supermoderator" | "moderator" | "creator" | "user";

const ROLE_ALIASES: Record<string, NormalizedRole> = {
  admin: "admin",
  administrator: "admin",
  supermoderator: "supermoderator",
  "super moderator": "supermoderator",
  moderator: "moderator",
  mod: "moderator",
  "çizer üye": "creator",
  "cizer uye": "creator",
  creator: "creator",
  "content creator": "creator",
  user: "user",
  üye: "user",
};

const ROLE_RANK: Record<NormalizedRole, number> = {
  admin: 100,
  supermoderator: 80,
  moderator: 60,
  creator: 40,
  user: 10,
};

function normalizeRoleValue(raw: string | null | undefined): NormalizedRole | null {
  if (!raw) {
    return null;
  }

  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) {
    return null;
  }

  return ROLE_ALIASES[trimmed] ?? (["admin", "supermoderator", "moderator", "creator", "user"].includes(trimmed) ? (trimmed as NormalizedRole) : null);
}

function appendUnique(target: NormalizedRole[], value: NormalizedRole | null) {
  if (!value) {
    return;
  }
  if (!target.includes(value)) {
    target.push(value);
  }
}

export function extractRoles(payload: any): NormalizedRole[] {
  const normalized: NormalizedRole[] = [];
  const arraySource = payload?.roles ?? payload?.Roles;
  if (Array.isArray(arraySource)) {
    arraySource.forEach((role) => appendUnique(normalized, normalizeRoleValue(String(role ?? ""))));
  }

  if (!normalized.length && (payload?.role ?? payload?.Role)) {
    appendUnique(normalized, normalizeRoleValue(String(payload?.role ?? payload?.Role ?? "")));
  }

  return normalized;
}

export function resolvePrimaryRole(roles: NormalizedRole[]): NormalizedRole | "" {
  if (!roles.length) {
    return "";
  }
  return [...roles].sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a])[0];
}

export function hasAnyRole(roles: NormalizedRole[], required: NormalizedRole | NormalizedRole[]): boolean {
  const list = Array.isArray(required) ? required : [required];
  return roles.some((role) => list.includes(role));
}

export function hasRoleAtLeast(roles: NormalizedRole[], minRole: NormalizedRole): boolean {
  return roles.some((role) => ROLE_RANK[role] >= ROLE_RANK[minRole]);
}

export function normalizeRoleInput(role: string): NormalizedRole | null {
  return normalizeRoleValue(role);
}

export function getRoleRank(role: NormalizedRole): number {
  return ROLE_RANK[role] ?? 0;
}
